import { trackEvent } from "@analytics";
import { Body, Box, Icon, Link } from "@biom3/react";
import { useWeeklyRewards } from "../WeeklyRewardsProvider";

type AccountFlaggedReason = "sybil" | "sanctioned";

const FLAGGED_ACCOUNT_MESSAGE_MAP: Record<
  AccountFlaggedReason,
  React.ReactNode
> = {
  sybil: (
    <>
      We have detected activity on your account that contravenes our Terms and
      Conditions. As a result, you are unable to participate in the Weekly
      Rewards program. If you believe this is a mistake, please reach out to our
      support team.{" "}
      <Link
        variant="primary"
        rc={
          <a
            href="https://support.immutable.com/hc/en-us/requests/new"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent({
                component: "Link",
                action: "Pressed",
                extras: {
                  control: "SybilSupportLink",
                  section: "FlaggedAccountMessage",
                  userJourney: "Rewards",
                },
              });
            }}
          />
        }
      >
        Contact Support
      </Link>
    </>
  ),
  sanctioned: (
    <>
      This service is unavailable in your detected region in compliance with
      regulations. For further assistance, visit the Immutable support page at{" "}
      <Link
        variant="primary"
        rc={
          <a
            href="https://support.immutable.com"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent({
                component: "Link",
                action: "Pressed",
                extras: {
                  control: "SanctionedSupportLink",
                  section: "FlaggedAccountMessage",
                  userJourney: "Rewards",
                },
              });
            }}
          />
        }
      >
        https://support.immutable.com
      </Link>
    </>
  ),
};

export function FlaggedAccountMessage() {
  const { eligibility } = useWeeklyRewards();

  // Give sanctions more credence because its more severe
  const flagReason =
    eligibility?.rules?.is_not_sanctioned === false
      ? "sanctioned"
      : eligibility?.rules?.is_not_sybil === false
        ? "sybil"
        : undefined;

  if (!flagReason) {
    return null;
  }

  const message = FLAGGED_ACCOUNT_MESSAGE_MAP[flagReason];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "base.spacing.x2",
        maxWidth: "930px",
        mt: "base.spacing.x2",
        position: "relative",
      }}
    >
      <Icon
        icon="Alert"
        variant="bold"
        sx={{
          marginTop: "base.spacing.x1",
          width: "base.icon.size.300",
          aspectRatio: "1/1",
          fill: "base.color.status.fatal.bright",
        }}
      />
      <Body size="small">{message}</Body>
    </Box>
  );
}
